/**
 * Type definitions for Tables store
 * 
 * This file provides TypeScript interfaces for table-related data
 * to ensure consistent typing throughout the application.
 */

/**
 * Raw table definition data as stored in the database
 */
export interface TableDefinitionData {
  id: string;
  topic_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    client_id?: string;
    last_update?: string;
    [key: string]: any;
  };
  isDeleted?: boolean; // Used for offline tracking only
}

/**
 * Raw table column data as stored in the database
 */
export interface TableColumnData {
  id: string;
  table_definition_id: string;
  name: string;
  type: string;
  order_index: number;
  width?: number;
  is_visible: boolean;
  options?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  metadata?: {
    client_id?: string;
    [key: string]: any;
  };
  isDeleted?: boolean; // Used for offline tracking only
}

/**
 * Raw table row data as stored in the database
 */
export interface TableRowData {
  id: string;
  table_definition_id: string;
  order_index: number;
  created_at?: string;
  updated_at?: string;
  metadata?: {
    client_id?: string;
    [key: string]: any;
  };
  isDeleted?: boolean; // Used for offline tracking only
}

/**
 * Raw table cell data as stored in the database
 */
export interface TableCellData {
  id: string;
  row_id: string;
  column_id: string;
  value: string;
  created_at?: string;
  updated_at?: string;
  metadata?: {
    client_id?: string;
    [key: string]: any;
  };
  isDeleted?: boolean; // Used for offline tracking only
}

/**
 * Formatted table definition for use in the UI
 */
export interface FormattedTableDefinition {
  id: string;
  topicId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Formatted table column for use in the UI
 */
export interface FormattedTableColumn {
  id: string;
  tableDefinitionId: string;
  name: string;
  type: string;
  orderIndex: number;
  width?: number;
  isVisible: boolean;
  options?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Formatted table row for use in the UI
 */
export interface FormattedTableRow {
  id: string;
  tableDefinitionId: string;
  orderIndex: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Formatted table cell for use in the UI
 */
export interface FormattedTableCell {
  id: string;
  rowId: string;
  columnId: string;
  value: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Complete table data for a topic
 */
export interface CompleteTableData {
  tableDefinition: FormattedTableDefinition | null;
  columns: FormattedTableColumn[];
  rows: FormattedTableRow[];
  cells: FormattedTableCell[];
}

/**
 * Input for creating a new table definition
 */
export interface CreateTableDefinitionInput {
  topicId: string;
  name?: string;
}

/**
 * Input for updating a table definition
 */
export interface UpdateTableDefinitionInput {
  name?: string;
}

/**
 * Input for creating a new column
 */
export interface CreateTableColumnInput {
  tableDefinitionId: string;
  name?: string;
  type?: string;
  orderIndex?: number;
  isVisible?: boolean;
  options?: Record<string, unknown>;
  afterColumnId?: string; // For positioning relative to other columns
}

/**
 * Input for updating a column
 */
export interface UpdateTableColumnInput {
  name?: string;
  type?: string;
  orderIndex?: number;
  width?: number;
  isVisible?: boolean;
  options?: Record<string, unknown>;
}

/**
 * Input for creating a new row
 */
export interface CreateTableRowInput {
  tableDefinitionId: string;
  orderIndex?: number;
}

/**
 * Input for updating a row
 */
export interface UpdateTableRowInput {
  orderIndex?: number;
}

/**
 * Input for creating or updating a cell
 */
export interface UpdateTableCellInput {
  rowId: string;
  columnId: string;
  value: string;
}

/**
 * Helper function to format a table definition from database format to UI format
 */
export function formatTableDefinition(definition: TableDefinitionData): FormattedTableDefinition {
  return {
    id: definition.id,
    topicId: definition.topic_id,
    name: definition.name,
    createdAt: definition.created_at,
    updatedAt: definition.updated_at,
  };
}

/**
 * Helper function to format a table column from database format to UI format
 */
export function formatTableColumn(column: TableColumnData): FormattedTableColumn {
  return {
    id: column.id,
    tableDefinitionId: column.table_definition_id,
    name: column.name,
    type: column.type,
    orderIndex: column.order_index,
    width: column.width,
    isVisible: column.is_visible,
    options: column.options,
    createdAt: column.created_at,
    updatedAt: column.updated_at,
  };
}

/**
 * Helper function to format a table row from database format to UI format
 */
export function formatTableRow(row: TableRowData): FormattedTableRow {
  return {
    id: row.id,
    tableDefinitionId: row.table_definition_id,
    orderIndex: row.order_index,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Helper function to format a table cell from database format to UI format
 */
export function formatTableCell(cell: TableCellData): FormattedTableCell {
  return {
    id: cell.id,
    rowId: cell.row_id,
    columnId: cell.column_id,
    value: cell.value,
    createdAt: cell.created_at,
    updatedAt: cell.updated_at,
  };
}

/**
 * Type guard to check if data is CompleteTableData
 */
export function isCompleteTableData(data: unknown): data is CompleteTableData {
  if (!data || typeof data !== 'object') return false;
  
  const obj = data as any;
  
  // Check if it has the required structure
  if (!('tableDefinition' in obj) || !('columns' in obj) || !('rows' in obj) || !('cells' in obj)) {
    return false;
  }
  
  // Validate tableDefinition is null or has required fields
  if (obj.tableDefinition !== null) {
    if (!obj.tableDefinition || typeof obj.tableDefinition !== 'object') return false;
    if (!('id' in obj.tableDefinition) || !('topicId' in obj.tableDefinition)) return false;
  }
  
  // Validate arrays
  if (!Array.isArray(obj.columns) || !Array.isArray(obj.rows) || !Array.isArray(obj.cells)) {
    return false;
  }
  
  return true;
}

/**
 * Type guard to check if data is FormattedTableColumn
 */
export function isFormattedTableColumn(data: unknown): data is FormattedTableColumn {
  if (!data || typeof data !== 'object') return false;
  
  const obj = data as any;
  
  return (
    'id' in obj &&
    'tableDefinitionId' in obj &&
    'name' in obj &&
    'type' in obj &&
    'orderIndex' in obj &&
    typeof obj.orderIndex === 'number' &&
    'isVisible' in obj &&
    typeof obj.isVisible === 'boolean'
  );
}

/**
 * Helper function to format a table definition for sending to the database
 */
export function formatTableDefinitionForDatabase(definition: FormattedTableDefinition): TableDefinitionData {
  return {
    id: definition.id,
    topic_id: definition.topicId,
    name: definition.name,
    created_at: definition.createdAt,
    updated_at: definition.updatedAt,
  };
}

/**
 * Helper function to format a table column for sending to the database
 */
export function formatTableColumnForDatabase(column: FormattedTableColumn): TableColumnData {
  return {
    id: column.id,
    table_definition_id: column.tableDefinitionId,
    name: column.name,
    type: column.type,
    order_index: column.orderIndex,
    width: column.width,
    is_visible: column.isVisible,
    options: column.options,
    created_at: column.createdAt,
    updated_at: column.updatedAt,
  };
}

/**
 * Helper function to format a table row for sending to the database
 */
export function formatTableRowForDatabase(row: FormattedTableRow): TableRowData {
  return {
    id: row.id,
    table_definition_id: row.tableDefinitionId,
    order_index: row.orderIndex,
    created_at: row.createdAt,
    updated_at: row.updatedAt,
  };
}

/**
 * Helper function to format a table cell for sending to the database
 */
export function formatTableCellForDatabase(cell: FormattedTableCell): TableCellData {
  return {
    id: cell.id,
    row_id: cell.rowId,
    column_id: cell.columnId,
    value: cell.value,
    created_at: cell.createdAt,
    updated_at: cell.updatedAt,
  };
}